<template>
  <slot v-bind="{ items: filteredItems }" />
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';

import { escapeRegExp } from 'lodash-es';
import getPositionsForRegex from '@utils/getPositionsForRegex';
import { HighlightableName } from './GridList/utils/types';

type QuickSearchItem = Record<string, any>;

type QuickSearchItemWithHighlight = QuickSearchItem & {
  nameWithHighlights: HighlightableName;
};

/**
 * Component to manage the quick search feature over entities.
 *
 * Handles search (filter) of entities by their name based on a search query from the user.
 * It returns a scoped slot named "items" with the filtered items.
 *
 * It is a functional component, it doesn't display anything
 */
export default defineComponent({
  name: 'QuickSearchManager',

  props: {
    items: {
      type: Array as PropType<Array<QuickSearchItem>>,
      validator: (prop) => prop === null || Array.isArray(prop),
      required: true,
    },

    query: {
      type: String,
      required: true,
    },

    /**
     * Property of item to query against
     * Can also be a function which should returns the property
     */
    searchProp: {
      type: [String, Function] as PropType<string | ((item: any) => string)>,
      default: 'name',
    },

    /**
     * Add an object with the positions of the query in the search prop
     * To be used by ag-grid
     */
    highlightQuery: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    normalizedQuery() {
      return this.query.toLowerCase();
    },
    filteredItems() {
      const { items, query, searchProp } = this;

      if (!query) {
        return items;
      }

      const searchFn = typeof searchProp === 'string' ? (item: Record<string, string>) => item[searchProp] : searchProp;

      const filteredItems: Array<QuickSearchItem> = query
        ? items.filter((item: QuickSearchItem) => searchFn(item).trim().toLowerCase().includes(this.normalizedQuery))
        : items;

      if (!this.highlightQuery) {
        return filteredItems;
      }

      /**
       * AG Grid does strict comparison (===) on the cell value to know if it has changed and re-render it.
       * Since we need the NameRenderer to highlight the searched text dynamically as the user types, we need to
       * re-render the name cell every time there's a new search.
       * If the name is a string, AG Grid doesn't trigger a re-render when adding highlighting because the content
       * hasn't changed. However, passing an object with the highlighting information, makes AG Grid render again
       * since it detects the highlight info has changed.
       */
      let filteredItemsWithHighlight: Array<QuickSearchItemWithHighlight> = [];
      const highlightingRegex = new RegExp(escapeRegExp(query), 'gim');

      filteredItemsWithHighlight = filteredItems.map((item) => {
        const itemText = searchFn(item);

        const positions = getPositionsForRegex(highlightingRegex, itemText);

        return {
          ...item,
          nameWithHighlights: {
            highlightingPositions: positions,
            text: itemText,
          },
        };
      });

      return filteredItemsWithHighlight;
    },
  },
});
</script>
